<template>
    <TprmModal
        open
        additional-class-name="ForceSubmissionProcedure"
        title="Force Processing?"
        :subtitle="entity.displayName"
        size="md"
        :allow-close="!submitting"
        :show-close-icon="!submitting"
        @close="cancel"
    >
        <p class="ForceSubmissionProcedure__body" data-test-id="ForceSubmissionProcedure__body">
            {{ bodyText }}
        </p>
        <div
            v-if="showSecondRequestWarning"
            class="ForceSubmissionProcedure__warning"
            data-test-id="ForceSubmissionProcedure__warning"
        >
            <EidIcon class="ForceSubmissionProcedure__warningIcon" :icon-src="warningIcon" />
            <p class="ForceSubmissionProcedure__warningText">
                {{ secondRequestWarning }}
            </p>
        </div>
        <template #footer>
            <NewButton
                variant="flat"
                color="neutral"
                :disabled="submitting"
                data-test-id="ForceSubmissionProcedure__cancel"
                @click="cancel"
            >
                Cancel
            </NewButton>
            <NewButton
                variant="fill"
                color="danger"
                :loading="submitting"
                data-test-id="ForceSubmissionProcedure__confirm"
                @click="confirm"
            >
                Force Processing
            </NewButton>
        </template>
    </TprmModal>
</template>

<style lang="scss">
    .ForceSubmissionProcedure {
        &__body {
            margin: 0;
            font-size: var(--text-body-size, 14px);
            line-height: 1.5;
            color: var(--eidQualityBlack);
        }

        // Same warning notice as RevertActionModal's.
        &__warning {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            margin-top: 16px;
            padding: 12px 14px;
            background: var(--eidImpactOrange-10);
            border: 1px solid var(--eidImpactOrange-25);
            border-radius: 8px;
            color: var(--eidQualityBlack);
        }

        &__warningIcon {
            flex-shrink: 0;
            width: 16px;
            height: 16px;
            margin-top: 3px;
            color: var(--eidImpactOrange);
        }

        &__warningText {
            margin: 0;
            font-size: var(--text-body-size, 14px);
            line-height: 1.5;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { getSnackbarActions } from '@evidentid/dashboard-commons/modules/snackbar';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { ON_DEMAND_VERIFICATION_TYPE } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { VerificationRequestType } from '@evidentid/tprm-portal-lib/models/notification-configuration';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import {
        AwaitingRequirement,
        isExhaustedByForce,
        leadsToNewRequest,
    } from '@/modules/entity-details/components/OpenVerificationBanner/helpers';
    import {
        FORCE_SUBMISSION_ERROR_MESSAGE,
        getForceSubmissionConflictMessage,
        getForceSubmissionExpiringSentence,
        getForceSubmissionNewLinkSentence,
        getForceSubmissionNonCompliantSentence,
        getForceSubmissionOnDemandSentence,
        getForceSubmissionReceivedSentence,
        getForceSubmissionSecondRequestWarning,
    } from '@/modules/entity-details/constants/forceSubmission.constants';
    import { ForceSubmissionStatus, useEntityDetailsStore } from '@/modules/entity-details/store';

    export default defineComponent({
        name: 'ForceSubmissionProcedure',
        components: { EidIcon, NewButton, TprmModal },
        props: {
            // ProcedureTarget passes `uuid` to every procedure; declared so Vue doesn't warn about it.
            uuid: { type: String, default: '' },
            rpName: { type: String, required: true },
            entity: { type: Object as PropType<Entity>, required: true },
            verificationId: { type: String, required: true },
            receivedCount: { type: Number, required: true },
            awaiting: { type: Array as PropType<AwaitingRequirement[]>, default: (): AwaitingRequirement[] => [] },
        },
        // `finish` means the verification may have changed and the caller should reload it.
        emits: [ 'abort', 'finish' ],
        data() {
            return { warningIcon: faTriangleExclamation };
        },
        computed: {
            status(): ForceSubmissionStatus | undefined {
                return useEntityDetailsStore().forceSubmissionStatus[this.rpName];
            },
            submitting(): boolean {
                return this.status?.status === OperationStatus.loading;
            },
            // Asked for the first time or again after a non-compliant result: no coverage to keep.
            nonCompliantNames(): string[] {
                return this.awaiting
                    .filter((requirement) => requirement.verificationType === VerificationRequestType.new
                        || requirement.verificationType === VerificationRequestType.nonCompliant)
                    .map((requirement) => requirement.name);
            },
            onDemandNames(): string[] {
                return this.awaiting
                    .filter((requirement) => requirement.verificationType === ON_DEMAND_VERIFICATION_TYPE)
                    .map((requirement) => requirement.name);
            },
            expiringNames(): string[] {
                return this.awaiting
                    .filter((requirement) => requirement.verificationType === VerificationRequestType.expiring)
                    .map((requirement) => requirement.name);
            },
            showSecondRequestWarning(): boolean {
                return isExhaustedByForce(this.awaiting);
            },
            bodyText(): string {
                const sentences = [ getForceSubmissionReceivedSentence(this.receivedCount) ];
                if (this.nonCompliantNames.length > 0) {
                    sentences.push(getForceSubmissionNonCompliantSentence(this.nonCompliantNames));
                }
                if (this.expiringNames.length > 0) {
                    sentences.push(getForceSubmissionExpiringSentence(this.expiringNames));
                }
                if (this.onDemandNames.length > 0) {
                    sentences.push(getForceSubmissionOnDemandSentence(this.onDemandNames));
                }
                if (leadsToNewRequest(this.awaiting)) {
                    sentences.push(getForceSubmissionNewLinkSentence());
                }
                return sentences.join(' ');
            },
            secondRequestWarning(): string {
                return getForceSubmissionSecondRequestWarning(this.entity.displayName);
            },
        },
        beforeUnmount(): void {
            useEntityDetailsStore().clearForceSubmissionStatus({ rpName: this.rpName });
        },
        methods: {
            cancel(): void {
                if (!this.submitting) {
                    this.$emit('abort');
                }
            },
            async confirm(): Promise<void> {
                await useEntityDetailsStore().forceSubmission({
                    rpName: this.rpName,
                    entityId: this.entity.id,
                    verificationId: this.verificationId,
                });
                if (this.status?.status === OperationStatus.success) {
                    this.$emit('finish');
                } else if (this.status?.conflict) {
                    getSnackbarActions().displaySnackbar({
                        message: getForceSubmissionConflictMessage(this.status.conflictReason ?? null),
                        success: false,
                    });
                    this.$emit('finish');
                } else {
                    getSnackbarActions().displaySnackbar({ message: FORCE_SUBMISSION_ERROR_MESSAGE, success: false });
                    this.$emit('abort');
                }
            },
        },
    });
</script>
